import { Offcanvas } from "bootstrap";
import { IconText } from "components/IconText";
import { RoundInstructionsDisplay } from "components/RoundInstructionsDisplay";
import { StimulusImage } from "components/game/StimulusSidebar";
import { PresetPromptDto } from "dtos/frankenstories/prompts/PresetPromptDto";
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useRef } from "react";
import { useDuplication } from "hooks/useDuplication";
import { Link } from "react-router-dom";
import { RouteLink } from "Routes";
import { ProBadge } from "components/subscriptions/ProBadge";
import { setPageLoadMessage } from "pages/Page";
import { GlobalContext } from "contexts/GlobalContext";
import { ConditionalTooltip } from "components/ConditionalTooltip";

export const ActivePrompt = (props: {
    prompt?: PresetPromptDto;
    setActivePrompt: Dispatch<SetStateAction<PresetPromptDto | undefined>>;
}) => {
    const { prompt, setActivePrompt } = props;

    const { addNotification, isAuthenticated, globalData } = useContext(GlobalContext);

    const { exportSettings } = useDuplication();

    const div = useRef<HTMLDivElement>(null);

    const selectPrompt = () => {
        if (prompt && globalData?.isSubscribed) {
            setPageLoadMessage("Game settings copied");

            const settings = {
                gameName: prompt.name,
                presetPromptId: prompt.id,
                promptText: prompt.promptText,
                imageId: prompt.imageId,
                imageUrl: prompt.imageUrl,
                roundInstructions: prompt.roundInstructions,
            };

            exportSettings(settings);

            setActivePrompt(undefined);
        }
    };

    const sharePrompt = () => {
        if (prompt) {
            const url = `${window.location.origin}${RouteLink.promptsTopic(
                prompt.topicFriendlyUrl,
                prompt.friendlyUrl,
            )}`;

            navigator.clipboard.writeText(url);

            // show toast
            addNotification({
                title: "Link copied",
                description: "Share it with your friends!",
                type: "success",
            });
        }
    };

    useEffect(() => {
        if (div.current) {
            const offcanvas = Offcanvas.getOrCreateInstance(div.current);

            if (prompt) {
                offcanvas.show();
            } else {
                offcanvas.hide();
            }

            div.current.addEventListener("hidden.bs.offcanvas", () => {
                setActivePrompt(undefined);
            });

            return () => {
                offcanvas.hide();
            };
        }
    }, [prompt, setActivePrompt]);

    return (
        <div ref={div} className="offcanvas offcanvas-end" id="active-prompt">
            {prompt && (
                <Fragment>
                    <div className="offcanvas-header">
                        <h3 className="h4 text-secondary">{prompt.name}</h3>
                        <button type="button" className="btn-close text-bg-dark" data-bs-dismiss="offcanvas"></button>
                    </div>

                    <div className="offcanvas-body">
                        <div className="vstack gap-4">
                            <StimulusImage
                                imageUrl={prompt.imageUrl}
                                prompt={prompt.promptText}
                                hideModal={true}
                                disabled={true}
                            />

                            <RoundInstructionsDisplay roundInstructions={prompt.roundInstructions} />

                            <div className="hstack gap-2 flex-wrap">
                                <button type="button" className="btn btn-dark icon-link" onClick={sharePrompt}>
                                    <IconText icon="fas fa-share" text="Share" />
                                </button>

                                <div className="hstack gap-2 flex-wrap">
                                    <ConditionalTooltip
                                        show={!globalData?.isSubscribed}
                                        text={
                                            isAuthenticated
                                                ? "Available with a Pro subscription"
                                                : "Available in Frankenstories Pro - Register to try for free"
                                        }
                                    >
                                        <button
                                            disabled={!globalData?.isSubscribed}
                                            type="button"
                                            className="btn btn-primary icon-link"
                                            onClick={selectPrompt}
                                        >
                                            <IconText icon="fas fa-file-import" text="Use prompt" />
                                        </button>
                                    </ConditionalTooltip>

                                    <ProBadge />
                                </div>
                            </div>

                            {prompt.lessonPageUrl && (
                                <div>
                                    <a href={prompt.lessonPageUrl} className="link p-0" target="_blank">
                                        View lesson on Writelike
                                    </a>
                                </div>
                            )}

                            {prompt.games.length > 0 && (
                                <div className="vstack gap-3">
                                    <h3 className="h4">Example games</h3>

                                    <ul className="mb-0">
                                        {prompt.games.map(g => (
                                            <li key={g.code}>
                                                <Link target="_blank" to={RouteLink.discoverHallOfFameStory(g.code)}>
                                                    {g.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </Fragment>
            )}
        </div>
    );
};
